import { useEffect, useState } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { HubConnectionBuilder, LogLevel, type HubConnection } from '@microsoft/signalr'
import { API_BASE } from './config'
import { tokenStore } from '../auth/tokenStore'
import { refreshSession } from '../auth/refresh'
import { qk } from './queryKeys'

/** Mirrors ChatEvents in backend Chat/ChatRealtime.cs. Payloads are ids only; we re-fetch. */
export const CHAT_EVENTS = {
  messageReceived: 'MessageReceived',
  messageUpdated: 'MessageUpdated',
  conversationRead: 'ConversationRead',
  typing: 'Typing',
  conversationUpdated: 'ConversationUpdated',
} as const

/** A typing heartbeat lives 6 s server-side; re-check just after so the hint clears without a poll. */
export const TYPING_CLEAR_MS = 6_500

/** Wires the hub's chat events to query invalidation, so push and polling share one data path. */
export function bindChatEvents(connection: Pick<HubConnection, 'on'>, qc: QueryClient) {
  const messages = (conversationId: string) => {
    void qc.invalidateQueries({ queryKey: qk.chat.messages(conversationId) })
    void qc.invalidateQueries({ queryKey: qk.chat.list })
  }
  connection.on(CHAT_EVENTS.messageReceived, (conversationId: string) => messages(conversationId))
  connection.on(CHAT_EVENTS.messageUpdated, (conversationId: string) => void qc.invalidateQueries({ queryKey: qk.chat.messages(conversationId) }))
  connection.on(CHAT_EVENTS.conversationRead, (conversationId: string) => void qc.invalidateQueries({ queryKey: qk.chat.messages(conversationId) }))
  connection.on(CHAT_EVENTS.typing, (conversationId: string) => {
    void qc.invalidateQueries({ queryKey: qk.chat.typing(conversationId) })
    setTimeout(() => void qc.invalidateQueries({ queryKey: qk.chat.typing(conversationId) }), TYPING_CLEAR_MS)
  })
  connection.on(CHAT_EVENTS.conversationUpdated, (conversationId: string) => {
    void qc.invalidateQueries({ queryKey: qk.chat.detail(conversationId) })
    void qc.invalidateQueries({ queryKey: qk.chat.list })
  })
}

export function buildChatHubConnection(): HubConnection {
  return new HubConnectionBuilder()
    .withUrl(`${API_BASE}/hubs/calls`, {
      // The hub handshake can't carry an Authorization header on every transport, so the token rides
      // the access_token query string (AuthSetup.cs accepts it under /hubs only). Refresh first if
      // it's about to lapse, the same pre-flight apiFetch does.
      accessTokenFactory: async () => {
        if (tokenStore.isExpiringSoon()) await refreshSession()
        return tokenStore.getAccessToken() ?? ''
      },
    })
    .withAutomaticReconnect()
    .configureLogging(LogLevel.Warning)
    .build()
}

/** Holds one hub connection for as long as the caller is mounted and reports whether it's live.
 * While live, callers can relax their polling to a slow safety net (see chatQueries). */
export function useChatRealtime(): boolean {
  const qc = useQueryClient()
  const [live, setLive] = useState(false)

  useEffect(() => {
    const connection = buildChatHubConnection()
    bindChatEvents(connection, qc)
    connection.onreconnecting(() => setLive(false))
    connection.onreconnected(() => {
      setLive(true)
      void qc.invalidateQueries({ queryKey: qk.chat.all }) // catch up on anything missed while down
    })
    connection.onclose(() => setLive(false))

    let stopped = false
    connection
      .start()
      .then(() => {
        if (!stopped) setLive(true)
      })
      .catch(() => {
        /* polling still runs; automatic reconnect only kicks in after a first successful start */
      })
    return () => {
      stopped = true
      setLive(false)
      void connection.stop()
    }
  }, [qc])

  return live
}
